import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Button, ButtonProps, CircularProgress, Stack } from "@mui/material";
import { AsyncContent, RoleComponentGuard, Search } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useDealParamsHooks, useNavigate } from "hooks";
import { debounce } from "lodash";
import {
  useDealIdSelector,
  useIsDealCharBarOpenSelect,
  usePickUserPermissions,
  useTaskManageActions,
  useTasksSelect,
  useViewActions
} from "store";
import { TaskQueries, UserPermissionType } from "store/types";

import EmptyCardTasks from "../../assets/images/EmptyCardTasks.png";
import { RoleName } from "../../constants";
import { Filters } from "./Filters/Filters";
import { Table } from "./Table/Table";

import "./TaskManage.styles.scss";

const buttonStyles: ButtonProps["sx"] = {
  height: "27px",
  fontSize: "16px",
  textTransform: "none",
  fontWeight: "400"
};

const defaultQueries: TaskQueries = {
  status: [],
  priority: null,
  deal: [],
  assignee: [],
  due_date: [],
  isMyOpen: false,
  isMyRecurring: false,
  insideDeal: false
};

export const TaskManage: React.FC<{ clientId?: string }> = ({ clientId }) => {
  const { getTasksAction, clearTasksAction, setCreateAction } = useTaskManageActions();
  const { setIsDealCharBarOpenAction } = useViewActions();
  const isDealCharBarOpen = useIsDealCharBarOpenSelect();
  const isInitialLoadStarted = useRef(false);

  const isDealSkope = useDealIdSelector();

  const [queries, setQueries] = useState<TaskQueries>(defaultQueries);
  const { dealId: paramDealId } = useDealParamsHooks();
  const taskManager = usePickUserPermissions("task_manager");

  const noAccessPermission = taskManager && taskManager === UserPermissionType.NO_ACCESS;
  const navigate = useNavigate();

  const updateQueries = useCallback(
    (data: Partial<TaskQueries>) => {
      setQueries({ ...queries, ...data });
    },
    [queries]
  );

  const onMyOpenTasks = useCallback(() => {
    updateQueries({ isMyOpen: !queries.isMyOpen, isMyRecurring: false });
  }, [updateQueries]);

  // const onRecurringTasks = useCallback(() => {
  //   updateQueries({ isMyRecurring: !queries.isMyRecurring, isMyOpen: false });
  // }, [updateQueries]);

  const onSearch = useCallback(
    (search: string) => {
      updateQueries({ search });
    },
    [updateQueries]
  );

  const onSearchDebounce = debounce(onSearch, 500);

  const { loading, action } = useActionHook(getTasksAction);

  const handleAddTask = useCallback(() => {
    if (clientId) {
      setCreateAction({
        isOpen: true,
        disableRedirectOnSubmit: true,
        entity_type: "client",
        entity_id: Number(clientId)
      });
    } else {
      setCreateAction({ isOpen: true, disableRedirectOnSubmit: true });
    }
  }, [setCreateAction, clientId]);
  const tasks = useTasksSelect();

  useEffect(() => {
    if (loading) {
      isInitialLoadStarted.current = true;
    }
  }, [loading]);

  const hasActiveFilters = useMemo(
    () =>
      !!queries.status.length ||
      queries.priority !== null ||
      !!queries.deal.length ||
      !!queries.assignee.length ||
      !!queries.due_date.length ||
      queries.isMyOpen ||
      queries.isMyRecurring ||
      !!queries.search,
    [queries]
  );

  const isEmpty = isInitialLoadStarted.current && !loading && !tasks.length && !hasActiveFilters;

  useEffect(() => {
    if (isEmpty && isDealCharBarOpen) {
      setIsDealCharBarOpenAction(false);
    }
  }, [isEmpty]);

  useEffect(() => {
    // If inside the deal and queries aren't updated prevent request
    const updatedQueries = { ...queries, client_id: clientId };
    if (paramDealId) {
      updatedQueries.deal = [{ id: paramDealId, title: "" }];
      updatedQueries.insideDeal = true;
    }

    action(updatedQueries);
  }, [queries, paramDealId]);

  useEffect(() => {
    return () => {
      clearTasksAction();
    };
  }, []);

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      {tasks.length > 0 || hasActiveFilters ? (
        <div className="task-manage" style={{ margin: !isDealSkope ? "0 2px 2px 2px" : "0" }}>
          <div className="head">
            <div className="filter-block">
              <Filters
                updateQueries={updateQueries}
                deal={queries.deal}
                status={queries.status}
                due_date={queries.due_date}
                assignee={queries.assignee}
                triggerStyles={buttonStyles}
              />
              <Button
                sx={{
                  ...buttonStyles,
                  backgroundColor: queries.isMyOpen ? "#dae0e2" : "transparent",
                  "&:hover": {
                    background: queries.isMyOpen ? "#dae0e2" : "transparent"
                  }
                }}
                onClick={onMyOpenTasks}
                variant="outlined"
                size="sm"
              >
                My Open Tasks
              </Button>
              {/* <Button
            sx={{
              backgroundColor: queries.isMyRecurring
                ? "#dae0e2"
                : "transparent",
            }}
            onClick={onRecurringTasks}
            variant="outlined"
            size="small">
            My recurring tasks
          </Button> */}
            </div>
            <div className="rest">
              <Search
                defaultValue={queries.search || ""}
                onChange={(e) => onSearchDebounce(e.target.value)}
                className="search"
              />
            </div>
          </div>
          <AsyncContent spinnerProps={{ size: 150 }} loading={loading} className="content">
            <Table refetchTable={() => setQueries({ ...queries })} tasks={tasks} />
          </AsyncContent>
        </div>
      ) : loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.taskManage.title}
          description={{
            text: EMPTY_CARD_CONTENT.taskManage.description,
            externalText: EMPTY_CARD_CONTENT.taskManage.externalDescription
          }}
          image={{
            src: EmptyCardTasks,
            sx: { width: 315, height: 125 }
          }}
          fitPage
          actionsElement={
            <Button variant="contained" onClick={handleAddTask} startIcon={<AddIcon />} sx={{ bgcolor: "#071F35" }}>
              Add Task
            </Button>
          }
        />
      )}
    </RoleComponentGuard>
  );
};
